import './SACreateEditUserModal.scss'
import {i18n} from "../../../../../Utils/i18n/i18n";
import LoadingSpinner from "../../../../LoadingSpinner/LoadingSpinner";
import React, {useEffect} from "react";
import {Modal} from "../../../../Smart/Modal/Modal";
import {getUserAPI} from "../../../../../Services/api";
import {GridReactContext} from "../../../../../Utils/DataGridWrapper/GridContext";
import {FormInput} from "../../../../Form/Input/FormInput";
import {Button} from "../../../../Form/Button/Button";
import {buttonColors} from "../../../../Pickers/InputTheme";
import {
    SA_updateUserAPI,
    SA_getAllOrganizationsForCompany,
    SA_checkPhoneAvailabilityAPI,
    SA_updateUserPhoneAPI
} from "../../../../../Services/SA_api";
import {PhoneEditModal} from "../../../../Users/PhoneEditModal/PhoneEditModal";
import {MultiSelectAutocomplete} from "../../../../Form/AutoComplete/AutoComplete";
import {InternationalPhoneInputDisplayOnly} from "../../../../Form/InternationalPhoneInputDisplayOnly/InternationalPhoneInputDisplayOnly";
import {FormControlLabel, IconButton, Switch} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import {get} from 'lodash'

/**
 * SuperAdmin modal to edit a user's profile fields, phone number, and organization membership.
 *
 * Reuses existing flows (no new endpoints): phone via the admin `PhoneEditModal` (SA-mode APIs);
 * org membership loaded from `getUserAPI().organizations` (selected) + `SA_getAllOrganizationsForCompany`
 * (options), and saved together with the profile through the existing `SA_updateUserAPI` (organizationIds).
 *
 * @param {object} props
 * @param {boolean} props.createEditUserModalOpen - Whether the modal is open.
 * @param {function} props.closeCreateEditUserModal - Close handler.
 * @param {function} props.onSuccess - Called after a successful save.
 * @param {boolean} props.editMode - True when editing an existing user.
 * @param {string} props.userId - The user being edited.
 * @returns {JSX.Element}
 */
export const SACreateEditUserModal = ({
                                        createEditUserModalOpen,
                                        closeCreateEditUserModal,
                                        onSuccess,
                                        editMode,
                                        userId
                                    }) => {
    const [isLoading, setIsLoading] = React.useState(false)
    const gridContext = React.useContext(GridReactContext)
    const [firstName, setFirstName] = React.useState('')
    const [lastName, setLastName] = React.useState('')
    const [email, setEmail] = React.useState('')
    const [identifier, setIdentifier] = React.useState('')
    const [phone, setPhone] = React.useState('')
    const [phoneCountryCode, setPhoneCountryCode] = React.useState('')
    const [isSuperAdmin, setIsSuperAdmin] = React.useState(false)
    const [isMfaRequired, setIsMfaRequired] = React.useState(false)
    const [employeeId, setEmployeeId] = React.useState('')
    const [idNumber, setIdNumber] = React.useState('')

    // Phone edit modal
    const [phoneModalOpen, setPhoneModalOpen] = React.useState(false)

    // Organization membership
    const [orgOptions, setOrgOptions] = React.useState([])
    const [selectedOrgs, setSelectedOrgs] = React.useState([])
    const [orgsLoaded, setOrgsLoaded] = React.useState(false)

    const editFirstName = (e) => setFirstName(e.target.value)
    const editLastName = (e) => setLastName(e.target.value)
    const editEmail = (e) => setEmail(e.target.value)
    const editMfaRequired = (e) => setIsMfaRequired(e.target.checked)

    /**
     * Loads the target user and derives the current USER-permission memberships.
     * @returns {Promise<object>} The fetched user (includes companyId + organizations).
     */
    const fetchUser = async () => {
        const user = await getUserAPI({userId})
        setFirstName(user.firstName)
        setLastName(user.lastName)
        setEmail(user.email)
        setIdentifier(user.identifier)
        setPhone(user.phone)
        setPhoneCountryCode(user.phoneCountryCode || '')
        setIsSuperAdmin(get(user, 'roles.SuperAdmin'))
        setIsMfaRequired(get(user, 'isMfaRequired'))
        setEmployeeId(user.employeeId || '')
        setIdNumber(user.idNumber || '')

        const memberships = (user.organizations || [])
            .filter(orgPermission => orgPermission.permission === 'USER' && orgPermission.organization)
            .map(orgPermission => ({value: orgPermission.organization.id, text: orgPermission.organization.orgName}))
        setSelectedOrgs(memberships)

        return user
    }

    /**
     * Loads all organizations in the user's company as multiselect options.
     * @param {string} companyId - The target user's company id.
     */
    const fetchOrgOptions = async (companyId) => {
        const {data} = await SA_getAllOrganizationsForCompany({companyId, paginationModel: {page: 0, pageSize: 100000}})
        // Exclude personal organizations (VPOs) - they must never appear as a membership option.
        setOrgOptions((data || [])
            .filter(org => !org.isPersonal)
            .map(org => ({value: org.id, text: org.orgName})))
        setOrgsLoaded(true)
    }

    const init = async () => {
        setIsLoading(true)
        if (editMode) {
            const user = await fetchUser()
            await fetchOrgOptions(user.companyId)
        }
        setIsLoading(false)
    }

    useEffect(() => {
        init()
    }, []);

    const getFilteredOrgs = ({query}) => {
        if (!query) {
            return orgOptions
        }
        return orgOptions.filter(org => org.text.toLowerCase().includes(query.toLowerCase()))
    }

    const onPhoneSaved = () => {
        // Phone + its derived identifier changed on the server; refetch for an accurate representation.
        fetchUser()
    }

    const onSave = async () => {
        setIsLoading(true)
        try {
            // Only send organizationIds once options have loaded, so a failed load can't
            // accidentally wipe memberships (backend treats omitted organizationIds as "unchanged").
            const organizationIds = orgsLoaded ? selectedOrgs.map(org => org.value) : undefined
            await SA_updateUserAPI({userId, firstName, lastName, email, isMfaRequired, employeeId, idNumber, organizationIds})
        } catch (e) {
            console.log(e)
        } finally {
            setIsLoading(false)
            gridContext.triggerTableRefresh()
            onSuccess()
        }
    }

    return <Modal
        open={createEditUserModalOpen}
        onClose={closeCreateEditUserModal}>
        <div className="SACreateEditUserModal modalWrapper">
            <div className="modalTitle">
                <div>{`${i18n.translate('createEditUserModal.titleEdit')} ${firstName} ${lastName}`}</div>
            </div>
            <div className="modalContent">
                {isLoading && <LoadingSpinner/>}
                <div className="inputsWrapper">
                    <FormInput
                        label={i18n.translate('createEditUserModal.firstName')}
                        className=""
                        value={firstName}
                        onChange={editFirstName}
                    />
                    <FormInput
                        label={i18n.translate('createEditUserModal.lastName')}
                        className=""
                        value={lastName}
                        onChange={editLastName}/>
                    <FormInput
                        label={i18n.translate('createEditUserModal.email')}
                        className=""
                        value={email}
                        onChange={editEmail}/>
                    <FormInput
                        disabled
                        label={i18n.translate('createEditUserModal.identifier')}
                        className=""
                        value={identifier}/>

                    <InternationalPhoneInputDisplayOnly
                        value={phone}
                        countryCode={phoneCountryCode}
                        label={i18n.translate('createEditUserModal.phone')}
                        endAdornment={
                            <IconButton
                                onClick={() => setPhoneModalOpen(true)}
                                size="small"
                                aria-label={i18n.translate('createEditUserModal.editPhone')}
                                data-testid="sa-edit-phone-button"
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        }
                    />

                    <FormInput
                        label={i18n.translate('createEditUserModal.employeeId')}
                        className=""
                        value={employeeId}
                        onChange={(e) => setEmployeeId(e.target.value)}/>
                    <FormInput
                        label={i18n.translate('createEditUserModal.idNumber')}
                        className=""
                        value={idNumber}
                        onChange={(e) => setIdNumber(e.target.value)}/>
                    <FormControlLabel control={<Switch
                        disabled={isSuperAdmin}
                        checked={isSuperAdmin || isMfaRequired}
                        onChange={editMfaRequired}
                    />} label={i18n.translate('createEditUserModal.mfaRequired')} />

                    {editMode && orgsLoaded &&
                        <div className="SACreateEditUserModal__organizations" data-testid="sa-organizations-section">
                            <MultiSelectAutocomplete
                                enableSelectAll
                                selectAllText={i18n.translate('createEditUserModal.selectAllOrganizations')}
                                label={i18n.translate('createEditUserModal.organizationsLabel')}
                                fetchOptions={getFilteredOrgs}
                                initialSelectedValues={selectedOrgs}
                                onSelectionChange={setSelectedOrgs}
                                dataTestId="sa-org-multiselect"/>
                        </div>
                    }
                </div>
            </div>
            <div className="modalFooter">
                <Button
                    color={buttonColors.YELLOW}
                    onClick={closeCreateEditUserModal}
                    text={i18n.translate('general.close')}
                />
                <Button
                    disabled={!firstName || !lastName || !identifier}
                    color={buttonColors.GREY}
                    onClick={onSave}
                    text={i18n.translate('general.saveChanges')}
                    dataTestId="sa-save-user-button"
                />
            </div>

            {editMode &&
                <PhoneEditModal
                    userId={userId}
                    currentPhone={phone || ''}
                    currentCountryCode={phoneCountryCode}
                    isOpen={phoneModalOpen}
                    onClose={() => setPhoneModalOpen(false)}
                    onSuccess={onPhoneSaved}
                    checkAvailabilityApi={SA_checkPhoneAvailabilityAPI}
                    updatePhoneApi={SA_updateUserPhoneAPI}
                />
            }
        </div>
    </Modal>
}
